/**
 * UpgradeModal — imperative "openUpgradeModal(feature)". Shows when the user
 * hits a hard daily cap (only Try-Ons has one today) or when we want to
 * proactively route them to Premium.
 */
import { createRoot } from "react-dom/client";
import { Crown, X, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

const FEATURE_LABEL = {
  tryons: "Try-Ons",
  calendar_suggest: "calendar outfit suggestions",
  grooming_gen: "grooming plans",
  makeup_gen: "makeup palettes",
  chat_messages: "AI Stylist chat",
  beauty_scan: "AI Beauty Scans",
  care_gen: "care routines",
  travel: "Travel Planner",
  family: "Family Wardrobes",
};

// Map backend upgrade_to plan_ids into human labels for the modal.
const PLAN_LABEL = {
  premium_monthly: "Premium (monthly)",
  premium_annual: "Premium (annual)",
  travel_pass: "Travel Pass",
  family: "Family plan",
};

/**
 * Open the upgrade modal.
 *  - `openUpgradeModal("tryons")` — legacy string form for daily-cap upgrades
 *  - `openUpgradeModal({ feature: "travel", upgrade_to: ["travel_pass","premium_annual"], message: "..." })`
 *     — new structured form for plan-required (402) responses from the backend.
 */
export function openUpgradeModal(payload) {
  const host = document.createElement("div");
  document.body.appendChild(host);
  const root = createRoot(host);
  const close = () => { root.unmount(); host.remove(); };
  const props = typeof payload === "string" ? { feature: payload } : (payload || {});
  root.render(<UpgradeModalUi {...props} onClose={close} />);
}

// Convenience for callers that already have an axios error response.
export function openUpgradeModalFromError(err, fallbackFeature) {
  const detail = err?.response?.data?.detail;
  if (detail && detail.error === "plan_required") {
    openUpgradeModal({
      feature: detail.feature || fallbackFeature,
      upgradeTo: detail.upgrade_to || [],
      message: detail.message,
    });
    return true;
  }
  if (fallbackFeature) openUpgradeModal(fallbackFeature);
  return false;
}

function UpgradeModalUi({ feature, upgradeTo, message, onClose }) {
  const label = FEATURE_LABEL[feature] || feature || "this feature";
  const hasPlans = Array.isArray(upgradeTo) && upgradeTo.length > 0;
  const planLabels = hasPlans ? upgradeTo.map((p) => PLAN_LABEL[p] || p) : null;
  return (
    <div
      className="fixed inset-0 z-[1000] bg-black/70 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="upgrade-modal"
    >
      <div className="w-full max-w-md bg-background border border-border p-6 sm:p-8 relative">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-muted-foreground hover:text-foreground"
          aria-label="Close"
          data-testid="upgrade-modal-close"
        ><X size={18} /></button>

        <p className="eyebrow flex items-center gap-2"><Crown size={11} /> Upgrade required</p>
        <p className="font-serif text-2xl sm:text-3xl mt-3 leading-tight" data-testid="upgrade-modal-title">
          {hasPlans
            ? <><em>{label}</em> is on {planLabels.join(" or ")}.</>
            : <>You&apos;ve hit today&apos;s cap on <em>{label}.</em></>}
        </p>
        <p className="text-sm text-muted-foreground mt-4">
          {message
            || (hasPlans
              ? `Upgrade to ${planLabels[0]} to unlock ${label}, or pick Premium for the full atelier.`
              : "Upgrade once — go unlimited across every AI feature, no ads, no waiting.")}
        </p>

        <ul className="mt-6 space-y-2 text-sm">
          <Perk text="Unlimited try-ons, chats, palettes and plans" />
          <Perk text="Skip every ad — permanently" />
          <Perk text="Priority AI processing + advanced Beauty modules" />
        </ul>

        <div className="mt-7 sm:mt-8 flex gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1 rounded-none uppercase tracking-[0.2em] text-xs h-11"
            data-testid="upgrade-modal-later"
          >
            Maybe later
          </Button>
          <a
            href={hasPlans ? `/plans?highlight=${encodeURIComponent(upgradeTo[0])}` : "/plans"}
            onClick={onClose}
            data-testid="upgrade-modal-cta"
            className="flex-1 inline-flex items-center justify-center gap-2 bg-accent text-accent-foreground uppercase tracking-[0.2em] text-xs h-11 hover:bg-accent/90"
          >
            Go to Plans <Sparkles size={13} />
          </a>
        </div>
      </div>
    </div>
  );
}

function Perk({ text }) {
  return (
    <li className="flex items-baseline gap-2">
      <span className="w-1.5 h-1.5 bg-accent" />
      <span>{text}</span>
    </li>
  );
}
